import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Sparkles,
  Shield,
  Zap,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
  Play
} from 'lucide-react';
import { useAppName, useAppLogoUrl } from '../hooks/useTenantBranding';
import Logo from '../components/common/Logo';
import { useTranslation } from '../i18n/useTranslation';
import { LanguagePicker } from '../i18n/LanguagePicker';

export default function LandingPage() {
  const navigate = useNavigate();
  const appName = useAppName();
  const appLogoUrl = useAppLogoUrl();
  const { t } = useTranslation();

  const handleLogin = () => {
    // Redirect to bookseller login page for passwordless authentication
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Logo variant="light" size="sm" alt={appName} customLogoUrl={appLogoUrl} />
            <div className="flex items-center gap-3">
              <LanguagePicker />
              <button
                onClick={handleLogin}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                {t('App.Landing.SignIn')}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              {t('App.Landing.BadgeText')}
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              {t('App.Landing.HeroTitle1')}
              <span className="block bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                {t('App.Landing.HeroTitle2')}
              </span>
              {t('App.Landing.HeroTitle3')}
            </h1>

            <p className="text-xl text-neutral-600 leading-relaxed">
              {t('App.Landing.HeroSubtitle', { appName })}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleLogin}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all font-semibold text-lg shadow-lg hover:shadow-xl"
              >
                {t('App.Landing.GetStarted')}
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
              <button className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-neutral-700 rounded-xl hover:bg-neutral-50 transition-all font-semibold text-lg border-2 border-neutral-200">
                <Play className="w-5 h-5" aria-hidden="true" />
                {t('App.Landing.WatchDemo')}
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-neutral-200">
              <div>
                <div className="text-3xl font-bold text-neutral-900">{t('App.Landing.StatStudentsCount')}</div>
                <div className="text-sm text-neutral-600">{t('App.Landing.StatStudents')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-neutral-900">{t('App.Landing.StatPublishersCount')}</div>
                <div className="text-sm text-neutral-600">{t('App.Landing.StatPublishers')}</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-neutral-900">{t('App.Landing.StatUptimeCount')}</div>
                <div className="text-sm text-neutral-600">{t('App.Landing.StatUptime')}</div>
              </div>
            </div>
          </div>

          {/* Right Column - Hero Image/Visual */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl transform rotate-3 opacity-10"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl p-8 border border-neutral-200">
              <div className="aspect-[4/3] bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl flex items-center justify-center">
                <div className="text-center space-y-4">
                  <BookOpen className="w-24 h-24 text-primary-600 mx-auto" aria-hidden="true" />
                  <div className="text-lg font-semibold text-neutral-700">{t('App.Landing.InteractiveReader')}</div>
                  <div className="flex gap-2 justify-center">
                    <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-secondary-600 rounded-full animate-pulse delay-75"></div>
                    <div className="w-3 h-3 bg-secondary-400 rounded-full animate-pulse delay-150"></div>
                  </div>
                </div>
              </div>

              {/* Floating Cards */}
              <div className="absolute -left-4 top-20 bg-white rounded-xl shadow-lg p-4 border border-neutral-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-success-600" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{t('App.Landing.OfflineReady')}</div>
                    <div className="text-xs text-neutral-500">{t('App.Landing.ReadAnywhere')}</div>
                  </div>
                </div>
              </div>

              <div className="absolute -right-4 bottom-20 bg-white rounded-xl shadow-lg p-4 border border-neutral-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-primary-600" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{t('App.Landing.FastSmooth')}</div>
                    <div className="text-xs text-neutral-500">{t('App.Landing.OptimizedRendering')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              {t('App.Landing.FeaturesTitle')}
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              {t('App.Landing.FeaturesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group p-8 bg-gradient-to-br from-primary-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-primary-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-primary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BookOpen className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureHybrid')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureHybridDesc')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 bg-gradient-to-br from-secondary-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-secondary-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-secondary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureFast')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureFastDesc')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 bg-gradient-to-br from-success-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-success-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-success-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureDRM')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureDRMDesc')}
              </p>
            </div>

            {/* Feature 4 */}
            <div className="group p-8 bg-gradient-to-br from-secondary-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-secondary-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-secondary-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureHighlights')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureHighlightsDesc')}
              </p>
            </div>

            {/* Feature 5 */}
            <div className="group p-8 bg-gradient-to-br from-warning-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-warning-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-warning-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Globe className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureOffline')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureOfflineDesc')}
              </p>
            </div>

            {/* Feature 6 */}
            <div className="group p-8 bg-gradient-to-br from-info-50 to-white rounded-2xl border-2 border-neutral-100 hover:border-info-200 transition-all hover:shadow-lg">
              <div className="w-14 h-14 bg-info-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t('App.Landing.FeatureRoles')}</h3>
              <p className="text-neutral-600 leading-relaxed">
                {t('App.Landing.FeatureRolesDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}></div>
            </div>

            <div className="relative z-10">
              <h2 className="text-4xl font-bold mb-4">
                {t('App.Landing.CTATitle')}
              </h2>
              <p className="text-xl mb-8 text-primary-100">
                {t('App.Landing.CTASubtitle', { appName })}
              </p>
              <button
                onClick={handleLogin}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 rounded-xl hover:bg-neutral-100 transition-all font-semibold text-lg shadow-lg"
              >
                {t('App.Landing.CTAButton')}
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <Logo variant="light" size="sm" alt={appName} customLogoUrl={appLogoUrl} />
              </div>
              <p className="text-sm">
                {t('App.Landing.FooterTagline')}
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t('App.Common.Product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterFeatures')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterPricing')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterDocs')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t('App.Common.Company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterAbout')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterBlog')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterCareers')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">{t('App.Common.Support')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterHelp')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterContact')}</button></li>
                <li><button type="button" className="hover:text-white transition-colors text-left bg-transparent border-0 p-0">{t('App.Landing.FooterPrivacy')}</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-neutral-800 pt-8 text-center text-sm">
            <p>{t('App.Auth.AllRightsReserved', { appName })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
